import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Users, Plus, Trash2, Edit2, Star, User, Mail, Phone, MapPin } from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function Beneficiaries() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBeneficiary, setEditingBeneficiary] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    relationship: "",
    email: "",
    phone: "",
    address: ""
  });

  useEffect(() => {
    fetchBeneficiaries();
  }, []);

  const fetchBeneficiaries = async () => {
    try {
      const response = await axios.get(`${API}/users/beneficiaries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBeneficiaries(response.data);
    } catch (error) {
      toast.error("Failed to load beneficiaries");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingBeneficiary) {
        await axios.put(`${API}/users/beneficiaries/${editingBeneficiary.beneficiary_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Beneficiary updated");
      } else {
        await axios.post(`${API}/users/beneficiaries`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Beneficiary added");
      }
      
      setDialogOpen(false);
      setEditingBeneficiary(null);
      setFormData({ name: "", relationship: "", email: "", phone: "", address: "" });
      fetchBeneficiaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save beneficiary");
    }
  };

  const handleEdit = (beneficiary) => {
    setEditingBeneficiary(beneficiary);
    setFormData({
      name: beneficiary.name,
      relationship: beneficiary.relationship,
      email: beneficiary.email,
      phone: beneficiary.phone || "",
      address: beneficiary.address || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (beneficiaryId) => {
    if (!confirm("Are you sure you want to remove this beneficiary?")) return;
    
    try {
      await axios.delete(`${API}/users/beneficiaries/${beneficiaryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Beneficiary removed");
      fetchBeneficiaries();
    } catch (error) {
      toast.error("Failed to remove beneficiary");
    }
  };

  const handleSetPrimary = async (beneficiaryId) => {
    try {
      await axios.put(`${API}/users/beneficiaries/${beneficiaryId}/primary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Primary beneficiary updated");
      fetchBeneficiaries();
    } catch (error) {
      toast.error("Failed to update primary beneficiary");
    }
  };

  const openNewDialog = () => {
    setEditingBeneficiary(null);
    setFormData({ name: "", relationship: "", email: "", phone: "", address: "" });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
              Beneficiaries
            </h1>
            <p className="text-muted-foreground">
              Manage who receives payments if you file a claim
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" onClick={openNewDialog} data-testid="add-beneficiary-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Beneficiary
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Young Serif, serif' }}>
                  {editingBeneficiary ? "Edit Beneficiary" : "Add Beneficiary"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="beneficiary-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="relationship">Relationship</Label>
                  <Input
                    id="relationship"
                    value={formData.relationship}
                    onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                    placeholder="e.g., Spouse, Parent, Landlord"
                    required
                    data-testid="beneficiary-relationship-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10"
                      required
                      data-testid="beneficiary-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (Optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="pl-10"
                      data-testid="beneficiary-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Address (Optional)</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="pl-10"
                      data-testid="beneficiary-address-input"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="btn-primary" data-testid="save-beneficiary-btn">
                    {editingBeneficiary ? "Save Changes" : "Add Beneficiary"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {beneficiaries.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-4">
            {beneficiaries.map((beneficiary) => (
              <Card key={beneficiary.beneficiary_id} className="card-organic">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 w-12 h-12 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold flex items-center gap-2">
                          {beneficiary.name}
                          {beneficiary.is_primary && (
                            <Badge className="bg-amber-100 text-amber-800">
                              <Star className="h-3 w-3 mr-1" />
                              Primary
                            </Badge>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">{beneficiary.relationship}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleEdit(beneficiary)}
                        data-testid={`edit-beneficiary-${beneficiary.beneficiary_id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(beneficiary.beneficiary_id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-beneficiary-${beneficiary.beneficiary_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      {beneficiary.email}
                    </div>
                    {beneficiary.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        {beneficiary.phone}
                      </div>
                    )}
                    {beneficiary.address && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        {beneficiary.address}
                      </div>
                    )}
                  </div>

                  {!beneficiary.is_primary && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full mt-4"
                      onClick={() => handleSetPrimary(beneficiary.beneficiary_id)}
                      data-testid={`set-primary-${beneficiary.beneficiary_id}`}
                    >
                      <Star className="h-4 w-4 mr-2" />
                      Set as Primary
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="card-organic">
            <CardContent className="p-12 text-center">
              <div className="bg-muted rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Beneficiaries Yet</h3>
              <p className="text-muted-foreground mb-4">
                Add beneficiaries who will receive claim payments on your behalf.
              </p>
              <Button className="btn-primary" onClick={openNewDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Beneficiary
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
